import { areaForPath, isWriteMethod } from "@/lib/admin-routes";
import { knownLevel } from "@/lib/permissions-snapshot";

const BASE_URL = process.env.BUN_PUBLIC_API_BASE_URL;

interface RequestBehaviour {
  nullOn404?: boolean;
  // The two "why was I denied?" endpoints must not re-enter the 403 handler:
  // a suspended account gets a 403 from them too, which would loop forever.
  silentOn403?: boolean;
}

export class ForbiddenError extends Error {
  constructor(readonly path: string) {
    super("Forbidden");
    this.name = "ForbiddenError";
  }
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// Several endpoints answer with a bare status and no body, so the status is
// the only thing worth branching on. The message keeps its old shape for
// callers that still match on "HTTP 404".
async function apiError(res: Response): Promise<ApiError> {
  const text = await res.text().catch(() => "");
  return new ApiError(res.status, text || `HTTP ${res.status}`);
}

export class BaseProvider {
  protected async request<T>(path: string, options?: RequestInit): Promise<T> {
    return (await this.doRequest<T>(path, options, {})) as T;
  }

  // Some endpoints use a 404 to mean "no record" rather than an error
  // (e.g. GET /v1/users/me/enforcement when the caller isn't banned).
  protected requestOrNullOn404<T>(
    path: string,
    options?: RequestInit,
    silentOn403 = false,
  ): Promise<T | null> {
    return this.doRequest<T>(path, options, { nullOn404: true, silentOn403 });
  }

  protected async requestSilentOn403<T>(
    path: string,
    options?: RequestInit,
  ): Promise<T> {
    return (await this.doRequest<T>(path, options, { silentOn403: true })) as T;
  }

  // Responses that aren't JSON (a CSV export, say) still need the same session
  // and permission handling, so they go through the same pipe.
  protected async requestBlob(
    path: string,
    options?: RequestInit,
  ): Promise<Blob> {
    const res = await this.send(path, options, {});
    if (!res.ok) throw await apiError(res);
    return res.blob();
  }

  private async doRequest<T>(
    path: string,
    options: RequestInit | undefined,
    behaviour: RequestBehaviour,
  ): Promise<T | null> {
    const res = await this.send(path, options, behaviour);

    if (behaviour.nullOn404 && res.status === 404) {
      return null;
    }

    if (!res.ok) {
      throw await apiError(res);
    }

    const text = await res.text();
    try {
      return JSON.parse(text) as T;
    } catch {
      return text as unknown as T;
    }
  }

  private async send(
    path: string,
    options: RequestInit | undefined,
    behaviour: RequestBehaviour,
  ): Promise<Response> {
    // Same rule the backend applies: GET needs read, everything else needs
    // write. Refusing here turns a guaranteed 403 into an error the page's
    // own onError handler already knows how to show. Anything we can't map,
    // or haven't loaded permissions for yet, goes to the server as before.
    const method = options?.method ?? "GET";
    if (isWriteMethod(method)) {
      const area = areaForPath(path);
      const level = area && knownLevel(area);
      if (level && level !== "write") {
        throw new ForbiddenError(path);
      }
    }

    const res = await fetch(`${BASE_URL}${path}`, {
      ...options,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...options?.headers,
      },
    });

    // 401 is now the only status that means the session itself is gone; the
    // server has already cleared the cookie by the time we see it.
    if (res.status === 401) {
      window.dispatchEvent(new CustomEvent("auth:unauthorized"));
      throw new Error("Unauthorized");
    }

    // 403 means logged in but not allowed — either this area's level is too
    // low or the account is suspended. The session stays; the layout decides
    // which of the two it was.
    if (res.status === 403) {
      if (!behaviour.silentOn403) {
        window.dispatchEvent(
          new CustomEvent("auth:forbidden", { detail: { path } }),
        );
      }
      throw new ForbiddenError(path);
    }

    return res;
  }
}
